import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, RefreshCw, Pencil, X, Save, Loader2, Timer, Workflow, ChevronUp, ChevronDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCallback, useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RoninPreviewProvider } from "@/lib/roninPreviewContext";
import RoninBox from "@/pages/RoninBox";

/**
 * Admin preview of Ronin Box — mounts the real `<RoninBox />` page so the coach can
 * QA cue / sequence / timing changes without leaving admin. Adds a "jump to edit"
 * floating button that appears whenever a cue is firing in the preview workout — click
 * it during the round when something feels off, fix the cue inline, save, the runner
 * auto-refreshes so the very next round picks up the new value.
 */
export default function RoninPreviewAdmin() {
  const [bump, setBump] = useState(0);
  const [currentCue, setCurrentCue] = useState(null);       // last cue the runner fired
  const [currentSequence, setCurrentSequence] = useState(null); // sequence currently mid-firing
  const [editing, setEditing] = useState(null);             // cue being edited
  const [editingSeq, setEditingSeq] = useState(null);       // sequence being edited

  // Stable callbacks the runner can call without forcing context re-creation per render
  const onCueFired = useCallback((cue) => { setCurrentCue(cue); }, []);
  const onSequenceChange = useCallback((seq) => { setCurrentSequence(seq); }, []);
  const contextValue = useMemo(() => ({ onCueFired, onSequenceChange }), [onCueFired, onSequenceChange]);

  const refresh = () => { setBump((b) => b + 1); setCurrentCue(null); setCurrentSequence(null); };

  return (
    <RoninPreviewProvider value={contextValue}>
      <div className="min-h-screen bg-[#050505] text-white">
        <div className="border-b border-[#27272A] px-6 py-3 flex items-center justify-between sticky top-0 z-50 bg-[#050505]">
          <div className="flex items-center gap-3">
            <Link to="/admin/ronin-box" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-ronin-admin">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Admin</p>
              <h1 className="font-display text-lg uppercase tracking-tight leading-none">Ronin preview</h1>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-3">Live · uses latest saved cues + sequences</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" onClick={refresh}
              className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs"
              data-testid="ronin-preview-refresh"
              title="Re-mount the runner so it re-fetches cues/sequences/settings"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <a href="/ronin-box" target="_blank" rel="noreferrer" data-testid="ronin-preview-open-tab">
              <Button className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs">
                <ExternalLink className="h-4 w-4 mr-2" /> Open in new tab
              </Button>
            </a>
          </div>
        </div>

        <div key={bump}>
          <RoninBox />
        </div>

        {/* Floating "Edit current cue" — portal'd to body so the runner's fullscreen container
            can't clip or hide it via stacking context. Only renders when a cue has fired. */}
        {currentCue && !editing && createPortal(
          <button
            onClick={() => setEditing(currentCue)}
            className="fixed top-20 right-6 z-[9999] bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs px-4 py-3 rounded-md shadow-xl border-2 border-white/10 flex items-center gap-2"
            data-testid="ronin-preview-edit-current"
            title={`Jump to editor for "${currentCue.text}"`}
          >
            <Pencil className="h-4 w-4" />
            <div className="text-left leading-tight">
              <div className="text-[9px] tracking-widest text-white/70">Edit current cue</div>
              <div className="text-sm font-bold normal-case tracking-tight">{currentCue.text}</div>
            </div>
          </button>,
          document.body
        )}

        {/* Floating "Edit current sequence" — portal'd to body, sits just below the cue button.
            Only renders while a multi-step sequence is mid-firing (single-cue sequences end on
            the first step so the activeSequence flips back to null immediately). */}
        {currentSequence && !editingSeq && !editing && createPortal(
          <button
            onClick={() => setEditingSeq(currentSequence)}
            className="fixed top-[140px] right-6 z-[9999] bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-xs px-4 py-3 rounded-md shadow-xl border-2 border-white/10 flex items-center gap-2"
            data-testid="ronin-preview-edit-sequence"
            title={`Jump to editor for "${currentSequence.name}"`}
          >
            <Workflow className="h-4 w-4" />
            <div className="text-left leading-tight">
              <div className="text-[9px] tracking-widest text-black/70">Edit current sequence</div>
              <div className="text-sm font-bold normal-case tracking-tight">{currentSequence.name}</div>
            </div>
          </button>,
          document.body
        )}

        {editing && (
          <QuickCueEditor
            cue={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
          />
        )}

        {editingSeq && (
          <QuickSequenceEditor
            sequence={editingSeq}
            onClose={() => setEditingSeq(null)}
            onSaved={() => { setEditingSeq(null); refresh(); }}
          />
        )}
      </div>
    </RoninPreviewProvider>
  );
}

/**
 * Focused inline cue editor — the fields the coach is most likely to tune mid-workout:
 * text, punches, movement_count, cue_seconds override. Everything else (modes, levels,
 * audio) stays on the full editor at /admin/ronin-box.
 */
function QuickCueEditor({ cue, onClose, onSaved }) {
  const [text, setText] = useState(cue.text || "");
  const [punches, setPunches] = useState(cue.punches ?? 0);
  const [movement, setMovement] = useState(cue.movement_count ?? 0);
  const [seconds, setSeconds] = useState(cue.cue_seconds == null ? "" : cue.cue_seconds);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        text: text.trim() || cue.text,
        punches: Math.max(0, Math.floor(Number(punches) || 0)),
        movement_count: Math.max(0, Math.floor(Number(movement) || 0)),
        cue_seconds: seconds === "" || seconds === null ? null : Math.max(0.5, Math.min(15, Number(seconds))),
      };
      await api.patch(`/admin/ronin-box/cues/${cue.id}`, payload);
      toast.success("Saved — runner will refresh");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-md" data-testid="ronin-preview-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-white uppercase tracking-tight">Edit cue</DialogTitle>
          <p className="text-[10px] text-zinc-500 uppercase tracking-widest">{cue.category} · saving refreshes the runner</p>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Text</label>
            <Input value={text} onChange={(e) => setText(e.target.value)}
              className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1"
              data-testid="ronin-preview-edit-text" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Punches</label>
              <Input type="number" min="0" max="10" value={punches} onChange={(e) => setPunches(e.target.value)}
                className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1"
                data-testid="ronin-preview-edit-punches" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Movements</label>
              <Input type="number" min="0" max="10" value={movement} onChange={(e) => setMovement(e.target.value)}
                className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1"
                data-testid="ronin-preview-edit-movement" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Secs</label>
              <Input type="number" min="0.5" max="15" step="0.1" value={seconds} onChange={(e) => setSeconds(e.target.value)}
                placeholder="Auto"
                className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1"
                data-testid="ronin-preview-edit-seconds" />
            </div>
          </div>
          <p className="text-[10px] text-zinc-600 inline-flex items-center gap-1">
            <Timer className="h-3 w-3" /> Leave Secs blank to use auto (punch/movement default). Range 0.5–15s.
          </p>

          <div className="flex items-center justify-between pt-3 border-t border-[#27272A]">
            <a
              href="/admin/ronin-box"
              className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-white"
              data-testid="ronin-preview-edit-full"
            >
              Open full editor →
            </a>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose} className="border-[#27272A] bg-transparent text-zinc-300 uppercase tracking-widest font-bold text-xs" data-testid="ronin-preview-edit-cancel">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" data-testid="ronin-preview-edit-save">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Focused inline sequence editor — reorder, remove, add steps. Save → preview refreshes.
 * The user CTA here is "drop a step that feels wrong" mid-workout; the full editor at
 * `/admin/ronin-box/sequences` is still one click away for renaming / activating / deleting.
 */
function QuickSequenceEditor({ sequence, onClose, onSaved }) {
  const [cueIds, setCueIds] = useState([...(sequence.cue_ids || [])]);
  const [addCueId, setAddCueId] = useState("");
  const [cues, setCues] = useState([]);
  const [saving, setSaving] = useState(false);

  // Lazy-load the cue library so the dropdown can show every cue (cheap, single GET, cached server-side)
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/ronin-box/cues").then((res) => {
      if (!cancelled) setCues((res.data || []).filter((c) => c.active));
    });
    return () => { cancelled = true; };
  }, []);

  const cueById = useMemo(() => {
    const m = {};
    for (const c of cues) m[c.id] = c;
    return m;
  }, [cues]);

  const groupedCues = useMemo(() => {
    const m = {};
    for (const c of cues) (m[c.category] = m[c.category] || []).push(c);
    return m;
  }, [cues]);

  const addStep = () => {
    if (!addCueId) return;
    setCueIds((arr) => [...arr, addCueId]);
    setAddCueId("");
  };
  const removeStep = (idx) => setCueIds((arr) => arr.filter((_, i) => i !== idx));
  const moveStep = (idx, dir) => {
    setCueIds((arr) => {
      const next = [...arr];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return arr;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (cueIds.length === 0) { toast.error("Sequence needs at least one step"); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/ronin-box/sequences/${sequence.id}`, { cue_ids: cueIds });
      toast.success("Sequence saved — runner will refresh");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-xl max-h-[85vh] overflow-y-auto" data-testid="ronin-preview-edit-seq-dialog">
        <DialogHeader>
          <DialogTitle className="text-white uppercase tracking-tight">Edit · {sequence.name}</DialogTitle>
          <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Reorder · drop · add steps · saving refreshes the runner</p>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Steps · in order</label>
          {cueIds.length === 0 ? (
            <p className="text-xs text-zinc-600 italic">No steps — add one below.</p>
          ) : (
            <ol className="space-y-1" data-testid="ronin-preview-seq-steps">
              {cueIds.map((id, idx) => {
                const c = cueById[id];
                return (
                  <li key={`${id}-${idx}`} className="flex items-center gap-2 border border-[#27272A] bg-[#050505] rounded-sm pl-2 pr-1 py-1.5">
                    <span className="text-[10px] text-zinc-600 font-bold w-6 text-center">{idx + 1}</span>
                    <span className="flex-1 text-sm text-white truncate">{c ? c.text : <span className="text-red-400">[missing {id}]</span>}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-600">{c?.category}</span>
                    <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`ronin-preview-seq-up-${idx}`}>
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button onClick={() => moveStep(idx, +1)} disabled={idx === cueIds.length - 1} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`ronin-preview-seq-down-${idx}`}>
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    <button onClick={() => removeStep(idx)} className="p-1 text-zinc-500 hover:text-red-400" data-testid={`ronin-preview-seq-remove-${idx}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="flex items-center gap-2">
            <Select value={addCueId} onValueChange={setAddCueId}>
              <SelectTrigger className="bg-[#050505] border-[#27272A] text-white rounded-sm flex-1" data-testid="ronin-preview-seq-add-select">
                <SelectValue placeholder="Pick a cue to add…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white max-h-80">
                {Object.entries(groupedCues).map(([cat, list]) => (
                  <div key={cat}>
                    <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{cat}</div>
                    {list.map((c) => (<SelectItem key={c.id} value={c.id}>{c.text}</SelectItem>))}
                  </div>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={addStep} disabled={!addCueId} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs disabled:opacity-40" data-testid="ronin-preview-seq-add">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-[#27272A]">
            <a href="/admin/ronin-box/sequences" className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-white" data-testid="ronin-preview-seq-full">
              Open full sequence editor →
            </a>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose} className="border-[#27272A] bg-transparent text-zinc-300 uppercase tracking-widest font-bold text-xs" data-testid="ronin-preview-seq-cancel">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" data-testid="ronin-preview-seq-save">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

